import {
  Subject,
  SurveyData,
  SurveyMeta,
  SurveyQuery,
  SurveyReferral,
  SurveyTranslation,
  TemporaryAnswers,
} from '@surveycake/logic-engine';
import { defaultTheme, Theme } from '@surveycake/rc';
import { parseJwt } from '@surveycake/utils';
import { Wording } from '@surveycake/wording/survey';
import { SetPage } from 'app/core/page';
import { Exception, SetException } from 'app/core/status';
import { isDevelopment } from 'app/shared/utils/development';
import { initialData, initialPrivateData } from './initializer/data';
import { initialMeta } from './initializer/meta';
import { initialQuery } from './initializer/query';
import { initialReferral } from './initializer/referral';
import { initialTempAnswer } from './initializer/tempAnswer';
import { initialServerTime } from './initializer/serverTime';
import { initialTheme } from './initializer/theme';
import { initialTranslation } from './initializer/translation';
import { initialWording } from './initializer/wording';
import findNavigatorLanguageFromLanguageOthers from 'app/shared/utils/findNavigatorLanguageFromLanguageOthers';
import goToSurveyWithLang from 'app/shared/utils/goToSurveyWithLang';
import { tempAnswerEnable } from 'config/system';
import { parse } from 'qs';
import Cookies from 'js-cookie';

/**
 * The service for survey config.
 *
 * Please see roadmap of `logic-engine`.
 */
export class SurveyConfig {
  /**
   * data
   */
  public query?: SurveyQuery;
  public referral?: SurveyReferral;
  public data: SurveyData = {} as SurveyData;
  public subjects: Subject[] = [];
  public meta?: SurveyMeta;
  public answers?: TemporaryAnswers;
  public theme: Theme = defaultTheme;
  public translation: SurveyTranslation = {};
  public wording: Wording = {} as Wording;
  public serverTime = '';

  constructor(
    public surveyHash: string,
    public referralToken?: string,
    public tempAnswerHash?: string,
  ) {}

  /**
   * To check whether to redirect to password page.
   */
  private _redirectToPassword() {
    return (
      this.data.status === 'PRIVATE' &&
      !(this.query && this.query.status === 'preview')
    );
  }

  /**
   * To check whether to redirect to third party login page.
   * Just `N` or `Y` now.
   * If need other services, refactor this method.
   */
  private _redirectToThirdPartyLogin() {
    return this.data.regneed;
  }

  /**
   * To check if the status of the survey is invalid.
   * If invalid, will return exception, or undefined.
   */
  private _validateSurveyStatus(): Exception | undefined {
    if (this.data.status === 'OFF') {
      return new Exception('NOT_PUBLIC');
    }

    if (
      typeof this.data.ppl_count !== 'undefined' &&
      typeof this.data.limitcount !== 'undefined' &&
      this.data.ppl_count >= this.data.limitcount
    ) {
      return new Exception('FULLED');
    } else if (
      (this.data.starttime &&
        this.serverTime &&
        this.data.starttime > this.serverTime) ||
      (this.data.endtime &&
        this.serverTime &&
        this.data.endtime < this.serverTime)
    ) {
      return new Exception('INVALID_DURATION');
    }

    return undefined;
  }

  getQueryThemeName() {
    return this.query && this.query.theme;
  }

  getQueryLanguage() {
    return this.query && this.query.lang;
  }

  isPreview() {
    return Boolean(this.query && this.query.status === 'preview');
  }

  isChildSurvey() {
    return Boolean(this.data.masterhash);
  }

  /**
   * To determine to redirect to other pages or throw exception.
   */
  handleStageFinished(
    setPage: SetPage,
    setException: SetException,
    nextStage: { password?: boolean; thirdPartyLogin?: boolean } = {}
  ) {
    const { password, thirdPartyLogin } = nextStage;

    if (password && this._redirectToPassword()) {
      setPage('password');
    } else if (thirdPartyLogin && this._redirectToThirdPartyLogin()) {
      setPage('thirdpartylogin');
    } else {
      const error = this._validateSurveyStatus();

      if (error) {
        setException(error);
      } else {
        setPage('index');
      }
    }
  }

  logSelf() {
    if (isDevelopment) {
      console.log('--- survey config ---', this);
    }
  }

  /**
   * Initial method for private survey.
   */
  async initialPrivateData(password: string) {
    try {
      const { data, subjects } = await initialPrivateData(
        this.surveyHash,
        password
      );
      this.data = data;
      this.subjects = subjects;
      this.logSelf();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Initial method.
   * Will throw exception after all initializers are settled.
   *
   * @todo Since `Promise.allSettled` is only supported on Chrome 76.
   * So the below is written in spaghetti style.
   */
  async initial() {
    let exception: Exception | undefined;

    const { jwt, bs_error } = parse(window.location.search, {
      ignoreQueryPrefix: true
    });

    // 聯發科由於不建議升級 logic-engine，query 沒有 jwt, bs_error 的 type，因此只好另外寫
    try {
      if (jwt) {
        // 這邊是半登入的狀態 (可填問卷不能操作後台)
        const { mbr_id, exp } = parseJwt(jwt);
        Cookies.set('mbr_id', mbr_id);
        // 判斷聯發科 token 是否過期，過期仍要刪除 mbr_id
        if (Date.now() > exp * 1000) {
          Cookies.remove('mbr_id');
        }
      }
      if (bs_error) {
        Cookies.remove('mbr_id');
      }
    } catch (error) {
      exception = exception || error;
    }

    try {
      this.query = initialQuery();
    } catch (error) {
      exception = exception || error;
    }

    try {
      if (this.tempAnswerHash && tempAnswerEnable) {
        this.answers = await initialTempAnswer(
          this.surveyHash,
          this.tempAnswerHash,
        );
      }
    } catch (error) {
      exception = exception || error;
    }

    try {
      if (this.referralToken) {
        this.referral = await initialReferral(
          this.surveyHash,
          this.referralToken
        );
      }
    } catch (error) {
      exception = exception || error;
    }

    try {
      const { data, subjects } = await initialData(
        this.surveyHash,
        this.isPreview()
      );
      this.data = data;
      this.subjects = subjects;
    } catch (error) {
      exception = exception || error;
    }

    const el = document.querySelector('html');

    if (((this.query && (this.query.lang === 'ar' || this.query.dir === 'rtl')) || this.data.language === 'ar') && el ){
      el.lang = 'ar';
    }

    try {
      this.meta = await initialMeta(this.surveyHash, this.isPreview());
    } catch (error) {
      exception = exception || error;
    }

    const queryLanguage = this.getQueryLanguage();

    if (
      queryLanguage &&
      this.meta &&
      this.meta.language &&
      this.meta.language.language_others &&
      this.meta.language.language_others.includes(queryLanguage)
    ) {
      try {
        this.translation = await initialTranslation(
          this.surveyHash,
          queryLanguage
        );
      } catch (error) {
        exception = new Exception('NOT_FOUND');
      }
    }

    if (
      queryLanguage &&
      this.meta &&
      this.meta.language &&
      this.meta.language.language_others &&
      !this.meta.language.language_others.includes(queryLanguage)
    ) {
      exception = new Exception('NOT_FOUND');
    }

    if (
      !queryLanguage && // 沒加 query 才開啟 auto_detect_navigator_language
      this.meta &&
      this.meta.language &&
      this.meta.language.language_others &&
      this.meta.language.language_others.length > 0 &&
      this.meta.language.auto_detect_navigator_language
    ) {
      const langOther = findNavigatorLanguageFromLanguageOthers(this.meta);
      if (langOther) {
        goToSurveyWithLang(langOther);
      }
    }

    try {
      this.theme = await initialTheme(
        this.getQueryThemeName(),
        this.data.theme
      );
    } catch (error) {
      exception = exception || error;
    }

    try {
      this.wording = await initialWording(queryLanguage, this.data.language);
    } catch (error) {
      exception = exception || error;
    }

    try {
      this.serverTime = await initialServerTime(this.surveyHash);
    } catch (error) {
      exception = exception || error;
    }

    this.logSelf();

    if (exception) {
      throw exception;
    }
  }
}
